import { useCallback, useRef } from 'react';
import useVendorStore from 'components/core/HomePage/useVendorStore';
import { LS_KEYS } from 'utils/constants/localStorageKeys';
import { ORDER_TYPE_TO_SERVICE_TYPE } from 'utils/constants/paymentOrderTypes';

/**
 * Smart hook for HP payment flow. Handles the full sequence internally:
 *   1. getAccessToken(orderType)  → opens ZPayKit (TOKEN event)      [HP API]
 *   2. fetchGuid(sdkPayload, cachedTokenResp) → caller-provided, returns a PaymentGuid
 *   3. getPaymentHash(orderType, guid, ...)   → hands off to SDK      [HP API]
 *
 *   ZPayKit SDK receives service_type (mapped from orderType via ORDER_TYPE_TO_SERVICE_TYPE).
 *
 * Returns props ready to spread onto <PaymentCheckout />.
 *
 * @param {object}   options
 * @param {number}   options.amount       — in paise
 * @param {string}   options.orderType    — HP-internal key from PAYMENT_ORDER_TYPES
 * @param {Function} options.fetchGuid    — async (sdkPayload, cachedTokenResp) => string (PaymentGuid)
 *                                          cachedTokenResp is the response from step 1 so simple cases
 *                                          can just return cachedTokenResp.PaymentGuid directly.
 * @param {number}   [options.orderPaymentEntityType=1]
 * @param {string}   [options.redirectUrl]
 * @param {Function} [options.onClose]
 * @param {Function} [options.onReady]
 * @param {Function} [options.onMessage]
 * @param {Function} [options.onBeforePayment] — async hook awaited inside the SDK's
 *                                               CHECKOUT handler before fetchGuid runs.
 *                                               Throw to abort the checkout (SDK closes
 *                                               the modal); caller is responsible for
 *                                               surfacing an error to the user.
 */
export function useHPPayment({
  amount,
  orderType,
  fetchGuid,
  orderPaymentEntityType = 1,
  redirectUrl,
  onClose,
  onReady,
  onMessage,
  onBeforePayment,
}) {
  const { getAccessToken, getPaymentHash } = useVendorStore();

  // HP orderType → ZPayKit service_type
  const serviceType = ORDER_TYPE_TO_SERVICE_TYPE[orderType] ?? orderType;

  // Cache accessToken response so step 3 can reuse PaymentHashParams without a second call
  const accessTokenRespRef = useRef(null);

  const userId = localStorage.getItem(LS_KEYS.USER_ID) ?? '';
  const phone  = localStorage.getItem(LS_KEYS.PHONE_NUMBER_SIGNIN) ?? '';
  const email  = localStorage.getItem(LS_KEYS.EMAIL) ?? '';

  // Step 1: HP API — getAccessToken uses orderType
  const getToken = useCallback(
    () =>
      new Promise((resolve, reject) =>
        getAccessToken(
          { orderType },
          (resp) => {
            const inner = resp?.response ?? resp;
            accessTokenRespRef.current = inner;
            resolve(inner.AccessToken ?? inner.token ?? '');
          },
          reject,
        ),
      ),
    [getAccessToken, orderType],
  );

  // Steps 2 + 3: called by SDK CHECKOUT event
  const onCheckoutRequest = useCallback(
    async (sdkPayload) => {
      if (onBeforePayment) {
        await onBeforePayment();
      }

      const cachedResp = accessTokenRespRef.current;

      // Step 2: domain-specific call — cachedResp passed so caller can use PaymentGuid directly
      const guid = await fetchGuid(sdkPayload, cachedResp);

      // Step 3: HP API — getPaymentHash uses orderType-derived params
      const paymentsHash = await new Promise((resolve, reject) =>
        getPaymentHash(
          {
            PaymentGuid: guid,
            PaymentHashParams: JSON.stringify(sdkPayload),
            OrderPaymentEntityType: orderPaymentEntityType,
          },
          resolve,
          reject,
        ),
      );

      console.log(paymentsHash)

      return {
        order_id: guid,
        amount,
        payments_hash: paymentsHash,
        service_type: serviceType,   // ZPayKit gets service_type
        country_id: 1,
        payment_method_id: sdkPayload.payment_method_id,
        payment_method_type: sdkPayload.payment_method_type,
        host_redirect_url: redirectUrl ?? `${window.location.origin}/buyer/payment/result`,
      };
    },
    [fetchGuid, getPaymentHash, amount, serviceType, orderPaymentEntityType, redirectUrl],
  );

  return {
    paymentInfo: {
      amount,
      formattedAmount: `₹${(amount ).toFixed(0)}`,
      buttonLabel: `Pay ₹${(amount ).toFixed(0)}`,
      service_type: serviceType,     // ZPayKit gets service_type
      order_type: serviceType,
      country_id: 1,
    },
    userInfo: { userId, phone, email },
    getToken,
    onCheckoutRequest,
    onClose,
    onReady,
    onMessage,
  };
}
